import { notification } from "antd";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { COMPANY_MESSAGES, QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import {
  AccountingPeriod,
  AccountingPeriodInput,
  BankInfo,
  BankInfoInput,
  ClientContractStatus,
  ClientContractStatusInput,
  ClientContractType,
  ClientContractTypeInput,
  ClientInput,
  Clients,
  ClientUpdateInput,
  CompanyInvoice,
  CompanyInvoiceInput,
  CompanyLeadership,
  CompanyLocation,
  CompanyName,
  CompanyPhoneNumber,
  CompanyPhoneNumberInput,
  CompanyRegistrationInfo,
  CompanyRegistrationInfoInput,
  IndustryCategories,
  MultipleClientDataInput,
  TaxInfo,
  TaxInfoInput,
} from "@lhd/types";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { Id } from "@silevis/reactgrid";

export const useFetchClients = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.CLIENTS, companyId, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.CLIENTS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          "q[company_id_eq]": companyId,
          current_company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as Clients[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useFetchOneClient = (clientId: any) => {
  return useQuery(
    [QUERY_NAME.CLIENT + "detailV1", clientId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.CLIENTS}/${clientId}`,
      }).then((res) => res.data as Clients),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!clientId,
    },
  );
};

export const useSubmitClient = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, clientId }: any) =>
      axios({
        method: clientId ? "patch" : "post",
        url: clientId ? `${SERVICES.CLIENTS}/${clientId}` : SERVICES.CLIENTS,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENTS]);
        queryClient.invalidateQueries([QUERY_NAME.CLIENT]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ clientId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.CLIENTS}/${clientId}`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENTS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleClient = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.CLIENTS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENTS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitClients = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "POST",
        url: SERVICES.CLIENTS + "/save",
        data: payload,
        headers: {
          Accept: "application/json",
        },
      }),
    {
      onSuccess(e) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENTS]);
        queryClient.invalidateQueries([QUERY_NAME.CLIENT]);
        if (e?.data?.errors?.length > 0) {
          notification["error"]({
            message: e?.data?.errors?.[0],
          });
        } else {
          // notification["success"]({
          //   message: "保存しました",
          // });
        }
      },
    },
  );
};

export const useSubmitClientsV2 = () => {
  return useMutation(({ payload }: { payload: ClientInput[] }) =>
    axiosInstanceV2({
      method: "POST",
      url: SERVICES.CLIENTS + "/save",
      data: {
        clients_data: payload,
        current_login_company_id: getCompanyId(),
      },
    }),
  );
};

export const useFetchOneClientV2 = (clientId: string) => {
  return useQuery(
    [QUERY_NAME.CLIENT + "detailV2", clientId],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.CLIENTS}/${clientId}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!clientId,
    },
  );
};
export const useSubmitClientV2 = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, id }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENTS}/${id}`,
        data: payload,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT + "detailV2"]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};
//client name
export const useFetchClientNames = (
  clientId: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.CLIENT_NAMES, clientId, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.CLIENT_NAMES,
        params: {
          client_id: clientId,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyName[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!clientId,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: clientId ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};
// Create client name (POST)
export const useCreateClientName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: SERVICES.CLIENT_NAMES,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_NAMES]);
        notification["success"]({ message: "顧客名を作成しました。" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Update client name (PATCH)
export const useUpdateClientName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_NAMES}/${id}`,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_NAMES]);
        notification["success"]({ message: "顧客名を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Delete client name (DELETE)
export const useDeleteClientName = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, clientId, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_NAMES}/${id}`,
        params: { client_id: clientId },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "顧客名を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_NAMES]);
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

export const useMultipleClientData = ({
  paginations,
  clientId,
}: MultipleClientDataInput) => {
  const results = useQueries({
    queries: [
      // Query 1: client phone numbers
      {
        queryKey: [
          QUERY_NAME.CLIENT_PHONE_NUMBERS,
          clientId,
          paginations.clientPhoneNumbers.page,
          paginations.clientPhoneNumbers.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_PHONE_NUMBERS}`,
            params: {
              client_id: clientId,
              page: paginations.clientPhoneNumbers.page,
              per_page: paginations.clientPhoneNumbers.pageSize,
            },
          }).then((res) => ({
            data: res.data as CompanyPhoneNumber[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 2: Invoices
      {
        queryKey: [
          QUERY_NAME.CLIENT_INVOICES,
          clientId,
          paginations.clientInvoices.page,
          paginations.clientInvoices.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_INVOICES}`,
            params: {
              client_id: clientId,
              page: paginations.clientInvoices.page,
              per_page: paginations.clientInvoices.pageSize,
            },
          }).then((res) => ({
            data: res.data as CompanyInvoice[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 3: BANK INFO
      {
        queryKey: [
          QUERY_NAME.CLIENT_BANK_INFOS,
          clientId,
          paginations.clientBankInfos.page,
          paginations.clientBankInfos.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_BANK_INFOS}`,
            params: {
              client_id: clientId,
              page: paginations.clientBankInfos.page,
              per_page: paginations.clientBankInfos.pageSize,
            },
          }).then((res) => ({
            data: res.data as BankInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 4: accountingPeriods
      {
        queryKey: [
          QUERY_NAME.CLIENT_ACCOUNTING_PERIODS,
          clientId,
          paginations.clientAccountingPeriods.page,
          paginations.clientAccountingPeriods.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_ACCOUNTING_PERIODS}`,
            params: {
              client_id: clientId,
              page: paginations.clientAccountingPeriods.page,
              per_page: paginations.clientAccountingPeriods.pageSize,
            },
          }).then((res) => ({
            data: res.data as AccountingPeriod[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 5: registrationInfo
      {
        queryKey: [
          QUERY_NAME.CLIENT_REGISTRATION_INFOS,
          clientId,
          paginations.clientRegistrationInfos.page,
          paginations.clientRegistrationInfos.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_REGISTRATION_INFOS}`,
            params: {
              client_id: clientId,
              page: paginations.clientRegistrationInfos.page,
              per_page: paginations.clientRegistrationInfos.pageSize,
            },
          }).then((res) => ({
            data: res.data as CompanyRegistrationInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // // Query 6: tax
      {
        queryKey: [
          QUERY_NAME.CLIENT_TAX_HISTORIES,
          clientId,
          paginations.clientTaxHistories.page,
          paginations.clientTaxHistories.pageSize,
        ],
        queryFn: () =>
          axiosInstanceV2({
            method: "get",
            url: `${SERVICES.CLIENT_TAX_HISTORIES}`,
            params: {
              client_id: clientId,
              page: paginations.clientTaxHistories.page,
              per_page: paginations.clientTaxHistories.pageSize,
            },
          }).then((res) => ({
            data: res.data as TaxInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // // Query 8: tax config
      {
        queryKey: [QUERY_NAME.COMPANIES + "industry_categories", 1, 1000000],
        queryFn: () =>
          axios({
            method: "get",
            url: `${SERVICES.COMPANIES}/industry_categories`,
            params: {
              page: 1,
              per_page: 1000000,
            },
          }).then((res) => {
            return {
              data: res.data as IndustryCategories[],
              meta: {
                total: res.headers["x-total"] || 0,
                total_page: res.headers["x-total-pages"] || 0,
              },
            };
          }),
        enabled: !!clientId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    ],
  });

  return {
    clientPhoneNumbers: {
      data: results[0]?.data?.data || [],
      meta: results[0]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[0]?.isLoading || false : false,
      isError: results[0]?.isError || false,
      error: results[0]?.error,
      dataUpdatedAt: results[0]?.dataUpdatedAt,
    },
    clientInvoices: {
      data: results[1]?.data?.data || [],
      meta: results[1]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[1]?.isLoading || false : false,
      isError: results[1]?.isError || false,
      error: results[1]?.error,
      dataUpdatedAt: results[1]?.dataUpdatedAt,
    },
    clientBankInfos: {
      data: results[2]?.data?.data || [],
      meta: results[2]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[2]?.isLoading || false : false,
      isError: results[2]?.isError || false,
      error: results[2]?.error,
      dataUpdatedAt: results[2]?.dataUpdatedAt,
    },
    clientAccountingPeriods: {
      data: results[3]?.data?.data || [],
      meta: results[3]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[3]?.isLoading || false : false,
      isError: results[3]?.isError || false,
      error: results[3]?.error,
      dataUpdatedAt: results[3]?.dataUpdatedAt,
    },
    clientRegistrationInfos: {
      data: results[4]?.data?.data || [],
      meta: results[4]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[4]?.isLoading || false : false,
      isError: results[4]?.isError || false,
      error: results[4]?.error,
      dataUpdatedAt: results[4]?.dataUpdatedAt,
    },
    clientTaxHistories: {
      data: results[5]?.data?.data || [],
      meta: results[5]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[5]?.isLoading || false : false,
      isError: results[5]?.isError || false,
      error: results[5]?.error,
      dataUpdatedAt: results[5]?.dataUpdatedAt,
    },
    industryCategories: {
      data: results[6]?.data?.data || [],
      meta: results[6]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: clientId ? results[6]?.isLoading || false : false,
      isError: results[6]?.isError || false,
      error: results[6]?.error,
      dataUpdatedAt: results[6]?.dataUpdatedAt,
    },

    isAnyLoading: results.some((result) => result.isLoading),
    isAnyError: results.some((result) => result.isError),
    allErrors: results.map((result) => result.error).filter(Boolean),
  };
};

// Company Phone Number hooks
export const useAddNewClientPhoneNumber = ({
  clientId,
}: {
  clientId: string;
}) => {
  return useMutation(
    (payload: CompanyPhoneNumberInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_PHONE_NUMBERS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "電話番号を追加しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};

export const useUpdateClientPhoneNumber = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyPhoneNumberInput>;
    }) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_PHONE_NUMBERS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_PHONE_NUMBERS]);
        notification["success"]({
          message: "電話番号を更新しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};

export const useDeleteClientPhoneNumber = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_PHONE_NUMBERS}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_PHONE_NUMBERS]);
        notification["success"]({
          message: "電話番号を削除しました",
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_PHONE_NUMBERS]);
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};

// client invoice hooks
export const useAddNewClientInvoice = ({ clientId }: { clientId: string }) => {
  return useMutation(
    (payload: CompanyInvoiceInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_INVOICES}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useUpdateClientInvoice = ({ clientId }: { clientId: string }) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyInvoiceInput>;
    }) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_INVOICES}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.INVOICE_LIST]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useDeleteClientInvoice = ({ clientId }: { clientId: string }) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_INVOICES}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.INVOICE_LIST]);
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
//bank info
export const useAddNewClientBankInfo = ({ clientId }: { clientId: string }) => {
  return useMutation(
    (payload: BankInfoInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_BANK_INFOS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_BANK_INFO_SUCCESS,
        });
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useUpdateClientBankInfo = ({ clientId }: { clientId: string }) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<BankInfoInput>;
    }) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_BANK_INFOS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_BANK_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useDeleteClientBankInfo = ({ clientId }: { clientId: string }) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_BANK_INFOS}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_BANK_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS]);
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};

// Fetch client leaderships (GET)
export const useFetchClientLeaderships = (
  clientId: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.CLIENT_LEADERSHIPS, clientId, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.CLIENT_LEADERSHIPS}`,
        params: {
          client_id: clientId,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyLeadership[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!clientId,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: clientId ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};

// Create client leadership (POST)
export const useCreateClientLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_LEADERSHIPS}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LEADERSHIPS]);
        notification["success"]({ message: "役員を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Update client leadership (PATCH)
export const useUpdateClientLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_LEADERSHIPS}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LEADERSHIPS]);
        notification["success"]({ message: "役員を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Delete client leadership (DELETE)
export const useDeleteClientLeadership = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, clientId, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_LEADERSHIPS}/${id}`,
        params: { client_id: clientId },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "役員を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LEADERSHIPS]);
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

//client accounting period hooks
export const useAddNewClientAccountingPeriod = ({
  clientId,
}: {
  clientId: string;
}) => {
  return useMutation(
    (payload: AccountingPeriodInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_ACCOUNTING_PERIODS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useUpdateClientAccountingPeriod = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<AccountingPeriodInput>;
    }) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_ACCOUNTING_PERIODS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_ACCOUNTING_PERIODS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useDeleteClientAccountingPeriod = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_ACCOUNTING_PERIODS}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_ACCOUNTING_PERIODS]);
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
//client registration info hooks
export const useAddNewClientRegistrationInfo = ({
  clientId,
}: {
  clientId: string;
}) => {
  return useMutation(
    (payload: CompanyRegistrationInfoInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_REGISTRATION_INFOS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useUpdateClientRegistrationInfo = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyRegistrationInfoInput>;
    }) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_REGISTRATION_INFOS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_REGISTRATION_INFOS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useDeleteClientRegistrationInfo = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_REGISTRATION_INFOS}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_REGISTRATION_INFOS]);
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
// client tax history hooks
export const useAddNewClientTaxHistories = ({
  clientId,
}: {
  clientId: string;
}) => {
  return useMutation(
    (payload: TaxInfoInput) =>
      axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENT_TAX_HISTORIES}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useUpdateClientTaxHistories = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<TaxInfoInput>;
    }) =>
      axiosInstanceV2({
        method: "put",
        url: `${SERVICES.CLIENT_TAX_HISTORIES}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, client_id: clientId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_TAX_HISTORIES]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useDeleteClientTaxHistories = ({
  clientId,
}: {
  clientId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_TAX_HISTORIES}/${id}`,
        params: {
          client_id: clientId,
        },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_TAX_HISTORIES]);
        notification["error"]({
          message: error?.response?.data?.message,
        });
      },
    },
  );
};

export const useUpdateClientStationId = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, clientId }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENTS}/${clientId}/update_station_id`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT + "detailV1"]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};

// Fetch client locations (GET)
export const useFetchClientLocations = (
  client_id: number,
  page = 1,
  per_page = 25,
  tabParams: string,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.CLIENT_LOCATIONS, client_id, page, per_page, tabParams],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.CLIENT_LOCATIONS,
        params: {
          client_id,
          page,
          per_page,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyLocation[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!client_id,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  return {
    ...queryResult,
    isLoading: client_id ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};

// Create client location (POST)
export const useCreateClientLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: SERVICES.CLIENT_LOCATIONS,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LOCATIONS]);
        notification["success"]({ message: "拠点を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Update client location (PATCH)
export const useUpdateClientLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CLIENT_LOCATIONS}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LOCATIONS]);
        notification["success"]({ message: "拠点を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

// Delete client location (DELETE)
export const useDeleteClientLocation = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, client_id, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_LOCATIONS}/${id}`,
        params: { client_id },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "拠点を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_LOCATIONS]);
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

//client contract status (GET)
export const useGetClientContractStatus = (
  clientId: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.CLIENT_CONTRACT_STATUSES, clientId, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "GET",
        url: SERVICES.CLIENT_CONTRACT_STATUSES,
        params: {
          client_id: clientId,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as ClientContractStatus[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!clientId,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: clientId ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};
// Create contract status (POST)
export const useCreateClientContractStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload }: { payload: ClientContractStatusInput }) =>
      axiosInstanceV2({
        method: "POST",
        url: SERVICES.CLIENT_CONTRACT_STATUSES,
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_STATUSES]);
        notification["success"]({ message: "契約状況を作成しました。" });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};
// Update contract status (PATCH)
export const useUpdateClientContractStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ClientContractStatusInput>;
    }) =>
      axiosInstanceV2({
        method: "PUT",
        url: `${SERVICES.CLIENT_CONTRACT_STATUSES}/${id}`,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_STATUSES]);
        notification["success"]({ message: "契約状況を更新しました" });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};
// Delete contract status (DELETE)
export const useDeleteClientContractStatus = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, clientId }: { id: number | string; clientId: number }) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_CONTRACT_STATUSES}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess() {
        notification["success"]({ message: "契約状況を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_STATUSES]);
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

//client contract type (GET)
export const useGetClientContractTypes = (
  clientId: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.CLIENT_CONTRACT_TYPES, clientId, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "GET",
        url: SERVICES.CLIENT_CONTRACT_TYPES,
        params: {
          client_id: clientId,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as ClientContractType[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!clientId,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: clientId ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};
// Create contract type (POST)
export const useCreateClientContractTypes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload }: { payload: ClientContractTypeInput }) =>
      axiosInstanceV2({
        method: "POST",
        url: SERVICES.CLIENT_CONTRACT_TYPES,
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_TYPES]);
        notification["success"]({ message: "契約タイプを作成しました。" });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};
// Update contract type (PATCH)
export const useUpdateClientContractTypes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ClientContractTypeInput>;
    }) =>
      axiosInstanceV2({
        method: "PUT",
        url: `${SERVICES.CLIENT_CONTRACT_TYPES}/${id}`,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_TYPES]);
        notification["success"]({ message: "契約タイプを更新しました" });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};
// Delete contract type (DELETE)
export const useDeleteClientContractTypes = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, clientId }: { id: number | string; clientId: number }) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.CLIENT_CONTRACT_TYPES}/${id}`,
        params: { client_id: clientId },
      }),
    {
      onSuccess() {
        notification["success"]({ message: "契約タイプを削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.CLIENT_CONTRACT_TYPES]);
        notification["error"]({ message: error?.response?.data?.msg });
        onError && onError(error);
      },
    },
  );
};

export interface ClientFilterOptionItem {
  value: string;
  label: string;
  count: number;
}

export interface ClientFilterOptionsResponse {
  success: boolean;
  filter_options: Record<string, ClientFilterOptionItem[]>;
}

export interface ClientFilterOptionsParams {
  current_login_company_id?: string | number;
  target_tab: string;
  field_key?: string;
  active_filters?: Record<string, string[]>;
  filter_order?: string[];
}

export const useFetchClientFilterOptions = (
  params: ClientFilterOptionsParams | null,
  enabled?: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.CLIENTS,
      "filter_options",
      params?.current_login_company_id || companyId,
      params?.target_tab,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CLIENTS}/filter_options`,
        data: {
          current_login_company_id:
            params.current_login_company_id || companyId,
          target_tab: params.target_tab,
          field_key: params.field_key,
          active_filters: params.active_filters || {},
          filter_order: params.filter_order || [],
        },
      });

      return res.data as ClientFilterOptionsResponse;
    },
    {
      enabled: !!params && enabled !== false,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
};

export const useFetchClientsV2 = (
  page: number,
  pageSize: number,
  target_tab: string,
  filterParams: any,
  options?: any,
) => {
  const companyId = getCompanyId();
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};
  return useQuery(
    [QUERY_NAME.CLIENTS, companyId, page, pageSize, target_tab, filterParams],
    () =>
      axiosInstanceV2({
        method: "GET",
        url: SERVICES.CLIENTS,
        params: {
          page,
          per_page: pageSize,
          current_login_company_id: companyId,
          target_tab,
          ...restFilterParams,
        },
      }).then((res) => ({
        data: res.data as Clients[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!target_tab && !__BLOCK_API__,
      ...(options || {}),
    },
  );
};

export const useSubmitClientSheets = () => {
  return useMutation(
    ({
      payload,
      tabParams,
    }: {
      payload: Partial<ClientUpdateInput>;
      tabParams: string;
    }) =>
      axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.CLIENT_SHEETS}/${tabParams}`,
        data: {
          current_login_company_id: getCompanyId(),
          client_data: payload,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.msg || "エラーが発生しました",
        });
      },
    },
  );
};

export const usePublicCustomerToCompany = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ clientId }: { clientId: Id }) =>
      axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.CLIENTS}/${clientId}/convert_to_company`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.CLIENTS]);
        queryClient.invalidateQueries([QUERY_NAME.ME]);
        notification["success"]({ message: "顧客を会社に変換しました" });
      },
    },
  );
};

export const useSaveClientSheets = () => {
  return useMutation(
    ({
      payload,
      tabParams,
    }: {
      payload: Partial<ClientUpdateInput>;
      tabParams: string;
    }) =>
      axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.CLIENT_SHEETS}/${tabParams}`,
        data: {
          current_login_company_id: getCompanyId(),
          client_data: payload,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        notification["success"]({ message: "契約タイプを更新しました" });
      },
      onError(error: any) {
        notification["error"]({ message: error?.response?.data?.msg });
      },
    },
  );
};

export const useFetchClientsSummary = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.CLIENTS, "summary", companyId],
    () =>
      axiosInstanceV2({
        method: "GET",
        url: `${SERVICES.CLIENTS}/summary`,
        params: {
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as Clients[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 0,
      staleTime: 0,
    },
  );
};
